import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Brain,
  Lightning,
  CloudRain,
  ShieldCheck,
  Scales,
  Waves,
  Drop,
} from "@phosphor-icons/react";
import FriendlyPulse from "@/components/v2/FriendlyPulse";
import useNowcastTimeline from "@/hooks/useNowcastTimeline";
import useFloodRisk from "@/hooks/useFloodRisk";
import { getHitRate, getMarine } from "@/lib/api";

/* =========================================================================
 * Phase 62 (May 8, 2026) — Pulse Page V2 (Pro re-arm).
 *
 * Vertical magazine-style scroll. Composes:
 *   1. Storm-motion tile (auto-show when optical-flow peak ≥ 8)
 *   2. Trust badge — Hit-rate ("88% accurate on 9,841 forecasts")
 *   3. Model-agreement tile — "Models agree / partly agree / disagree"
 *   4. FriendlyPulse hero + 6 horizontal comic panels (existing)
 *   5. Sea-pulse tile (auto-show for coastal locations)
 *   6. Flood-watch tile (auto-show when nearest river ≥ elevated)
 * ===================================================================== */

const NUM = {
  fontFamily: "Manrope, system-ui, sans-serif",
  fontWeight: 800,
  letterSpacing: "-0.04em",
  fontFeatureSettings: "'tnum' 1",
};

/* ---------------- shared section card ---------------- */

function Section({ title, accent, right, children, testId, delay = 0, theme }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.34, delay, ease: [0.22, 1, 0.36, 1] }}
      data-testid={testId}
      className="rounded-[18px] overflow-hidden"
      style={{
        background: theme.surface,
        border: `1px solid ${theme.surfaceBorder}`,
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        boxShadow: "0 1px 0 rgba(255,255,255,0.55) inset, 0 8px 22px -16px rgba(20,12,4,0.16)",
      }}
    >
      <header
        className="flex items-center gap-2 px-3.5 pt-2.5 pb-1.5"
        style={{ borderBottom: `1px solid ${theme.surfaceBorder}` }}
      >
        <span
          aria-hidden
          className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
          style={{ background: accent || theme.textMuted }}
        />
        <h3
          className="text-[10px] uppercase tracking-[0.18em] font-extrabold"
          style={{ color: theme.textSecondary }}
        >
          {title}
        </h3>
        {right && <div className="ml-auto">{right}</div>}
      </header>
      <div className="px-3.5 pt-3 pb-3.5">{children}</div>
    </motion.section>
  );
}

function Pill({ color, children, testId }) {
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-1 px-2.5 py-[3px] rounded-full text-[9.5px] font-extrabold uppercase tracking-[0.14em] whitespace-nowrap"
      style={{
        background: `${color}1f`,
        color,
        border: `1px solid ${color}66`,
      }}
    >
      {children}
    </span>
  );
}

/* ---------------- Storm-motion tile ---------------- */

function compass(deg) {
  if (deg == null) return "—";
  const d = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  return d[Math.round(deg / 45) % 8];
}

function StormMotionTile({ data, theme }) {
  if (!data) return null;
  const peak = Math.round(data.peak_intensity || 0);
  const peakMin = data.peak_minute || 0;
  const motion = data.storm_motion || {};
  const fromCompass = compass(motion.bearing_deg);
  const speed = motion.speed_kmh != null ? Math.round(motion.speed_kmh) : null;
  const accent = peak >= 60 ? "#dc2626" : peak >= 30 ? "#ea580c" : "#0369a1";
  const timeline = (data.timeline || []).slice(0, 7);
  const maxIntensity = Math.max(1, ...timeline.map((t) => t.intensity || 0));

  return (
    <Section
      title="Storm motion · next 30 min"
      accent={accent}
      right={<Pill color={accent} testId="storm-motion-peak">{peak}% peak</Pill>}
      testId="pulse-storm-motion"
      theme={theme}
      delay={0.02}
    >
      <div className="flex items-start gap-3">
        <div
          className="shrink-0 rounded-full flex items-center justify-center relative"
          style={{ width: 56, height: 56, background: `${accent}1f`, border: `2px solid ${accent}55` }}
        >
          <CloudRain size={26} weight="duotone" color={accent} />
          {motion.bearing_deg != null && speed > 0 && (
            <svg
              viewBox="0 0 60 60"
              className="absolute inset-0 w-full h-full"
              style={{ transform: `rotate(${motion.bearing_deg}deg)` }}
            >
              <path
                d="M 30 6 L 26 18 L 30 16 L 34 18 Z"
                fill={accent}
                stroke={accent}
                strokeWidth="0.6"
              />
            </svg>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-extrabold leading-tight" style={{ color: theme.textPrimary }}>
            {peak >= 30
              ? `Peak in ${peakMin} min · ${peak}% intensity`
              : `Light cells nearby · peak in ${peakMin} min`}
          </div>
          <div className="text-[11px] mt-1 leading-snug font-medium" style={{ color: theme.textSecondary }}>
            {speed > 0 && motion.bearing_deg != null
              ? `Cell drifting from ${fromCompass} at ${speed} km/h`
              : "Cells stationary — watch for new development"}
          </div>
          {timeline.length >= 3 && (
            <div className="mt-2 flex items-end gap-[3px] h-5">
              {timeline.map((t, i) => {
                const pct = Math.max(0.06, (t.intensity || 0) / maxIntensity);
                const isPeak = t.minute === peakMin && peak > 0;
                return (
                  <div
                    key={i}
                    className="flex-1 rounded-t-[2px]"
                    style={{
                      height: `${pct * 100}%`,
                      background: isPeak ? accent : `${accent}99`,
                      opacity: i === 0 ? 0.45 : 1,
                    }}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Section>
  );
}

/* ---------------- Trust / Hit-rate tile ---------------- */

function TrustTile({ stats, theme }) {
  // Require at least 20 verified samples — anything less is too noisy to
  // honestly badge as a "track record".
  if (!stats || stats.samples < 20 || stats.hit_rate == null) return null;
  const pct = Math.round(stats.hit_rate * 100);
  const accent = pct >= 80 ? "#16a34a" : pct >= 65 ? "#0891b2" : "#d97706";
  const samples = stats.samples;
  const blurb =
    pct >= 85
      ? "Industry-leading accuracy."
      : pct >= 70
        ? "Solid track record — trust the call."
        : "Forecasts are advisory — keep an eye out.";

  return (
    <Section
      title="Forecast trust · last 60 days"
      accent={accent}
      right={<Pill color={accent} testId="trust-hit-rate">{pct}% hit-rate</Pill>}
      testId="pulse-trust"
      theme={theme}
      delay={0.04}
    >
      <div className="flex items-start gap-3">
        <div
          className="shrink-0 rounded-full flex items-center justify-center"
          style={{ width: 56, height: 56, background: `${accent}1f`, border: `2px solid ${accent}55` }}
        >
          <ShieldCheck size={28} weight="duotone" color={accent} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <span className="tabular-nums leading-none" style={{ ...NUM, fontSize: 26, color: accent }}>
              {pct}%
            </span>
            <span className="text-[11px] font-bold tabular-nums" style={{ color: theme.textSecondary }}>
              correct on {samples.toLocaleString()} forecasts
            </span>
          </div>
          <div className="mt-1.5 h-1.5 rounded-full overflow-hidden" style={{ background: `${accent}22` }}>
            <motion.div
              className="h-full rounded-full"
              style={{ background: accent }}
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.9, ease: "easeOut" }}
            />
          </div>
          <div className="text-[10.5px] mt-1.5 leading-snug font-medium" style={{ color: theme.textMuted }}>
            {blurb}
          </div>
        </div>
      </div>
    </Section>
  );
}

/* ---------------- Model-agreement tile ---------------- */

function ModelAgreementTile({ forecast, theme }) {
  const agree = useMemo(() => {
    const arr = forecast?.hourly?.model_agreement || [];
    const spreads = forecast?.hourly?.temperature_2m_band_spread || [];
    if (!arr.length) return null;
    // Worst-case across the next 6 hours
    const window = arr.slice(0, 6);
    const spreadWindow = spreads.slice(0, 6).map((s) => (s == null ? 0 : s));
    const worst = window.includes("low") ? "low" : window.includes("moderate") ? "moderate" : "high";
    const peakSpread = Math.max(...spreadWindow, 0);
    return { worst, peakSpread, window, spreadWindow };
  }, [forecast]);

  if (!agree) return null;
  const { worst, peakSpread, window, spreadWindow } = agree;
  const accent = worst === "high" ? "#16a34a" : worst === "moderate" ? "#d97706" : "#dc2626";
  const label =
    worst === "high"
      ? "Models agree"
      : worst === "moderate"
        ? "Models partly agree"
        : "Models disagree";
  const blurb =
    worst === "high"
      ? "ECMWF + GFS + best are within ±1 °C — trust the forecast."
      : worst === "moderate"
        ? "Some uncertainty — the next 6 hours are confident, beyond is fuzzy."
        : "Atmosphere is volatile — keep an eye on the live picture.";

  // Bars now encode REAL per-hour spread (taller = more disagreement at that hour).
  // Caps at 5°C so a single outlier hour doesn't blow out the chart.
  const cap = Math.max(1, Math.min(5, peakSpread || 1));

  return (
    <Section
      title="Model consensus · next 6 hours"
      accent={accent}
      right={<Pill color={accent} testId="model-agreement-peak">±{peakSpread.toFixed(1)}°</Pill>}
      testId="pulse-model-agreement"
      theme={theme}
      delay={0.06}
    >
      <div className="flex items-start gap-3">
        <div
          className="shrink-0 rounded-full flex items-center justify-center"
          style={{ width: 56, height: 56, background: `${accent}1f`, border: `2px solid ${accent}55` }}
        >
          <Scales size={28} weight="duotone" color={accent} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-extrabold leading-tight" style={{ color: theme.textPrimary }}>
            {label}
          </div>
          <div className="mt-1.5 flex items-end gap-[3px] h-6" data-testid="model-agreement-bars" aria-label="Per-hour temperature spread across ECMWF + GFS + best-match">
            {window.map((a, i) => {
              const tone = a === "high" ? "#16a34a" : a === "moderate" ? "#d97706" : "#dc2626";
              // Height ∝ actual spread (with a 14% floor so bars stay visible
              // even when models perfectly agree at 0°)
              const heightPct = Math.max(14, Math.min(100, (spreadWindow[i] / cap) * 100));
              return (
                <div
                  key={i}
                  className="flex-1 rounded-t-[2px] flex items-end justify-center text-[7px] font-extrabold"
                  style={{ height: `${heightPct}%`, background: tone, color: "#fff" }}
                  title={`Hour ${i + 1}: ${a} · ±${spreadWindow[i].toFixed(1)}°`}
                >
                  <span className="tabular-nums leading-none pb-0.5" style={{ opacity: 0.85 }}>
                    {spreadWindow[i] >= 0.5 ? `${spreadWindow[i].toFixed(1)}°` : ""}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-1 mt-1">
            <span className="text-[8.5px] uppercase tracking-wider font-bold" style={{ color: theme.textMuted }}>+1h</span>
            <span className="flex-1 text-center text-[8.5px] tracking-wider font-bold" style={{ color: theme.textMuted }}>spread per hour</span>
            <span className="text-[8.5px] uppercase tracking-wider font-bold" style={{ color: theme.textMuted }}>+6h</span>
          </div>
          <div className="text-[10.5px] mt-1.5 leading-snug font-medium" style={{ color: theme.textMuted }}>
            {blurb}
          </div>
        </div>
      </div>
    </Section>
  );
}

/* ---------------- Sea pulse tile (coastal auto-show) ---------------- */

function SeaPulseTile({ marine, theme }) {
  if (!marine || !marine.available || !marine.current) return null;
  const cur = marine.current;
  const wave = cur.wave_height_m;
  if (wave == null) return null;
  const tone = marine.descriptor?.tone || "smooth";
  const accent =
    tone === "high" ? "#dc2626" :
    tone === "rough" ? "#f59e0b" :
    tone === "moderate" ? "#0e7490" :
    tone === "smooth" ? "#0891b2" :
    "#06b6d4";
  const fromCompass = compass(cur.wave_direction_deg);

  return (
    <Section
      title="Sea pulse · coastal"
      accent={accent}
      right={<Pill color={accent} testId="sea-pulse-tone">{(marine.descriptor?.label || "").split("—")[0].trim().toUpperCase()}</Pill>}
      testId="pulse-sea"
      theme={theme}
      delay={0.10}
    >
      <div className="flex items-start gap-3">
        <div
          className="shrink-0 rounded-full flex items-center justify-center"
          style={{ width: 56, height: 56, background: `${accent}1f`, border: `2px solid ${accent}55` }}
        >
          <Waves size={28} weight="duotone" color={accent} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <span className="tabular-nums leading-none" style={{ ...NUM, fontSize: 26, color: accent }}>
              {wave.toFixed(1)}
            </span>
            <span className="text-[11px] font-bold tabular-nums" style={{ color: theme.textSecondary }}>
              m wave
            </span>
            {marine.next24_peak_m != null && (
              <span className="ml-auto text-[10px] font-bold tabular-nums" style={{ color: theme.textMuted }}>
                Peak 24h {marine.next24_peak_m.toFixed(1)} m
              </span>
            )}
          </div>
          <div className="text-[11px] mt-1 font-medium leading-snug" style={{ color: theme.textSecondary }}>
            {marine.descriptor?.label}
          </div>
          <div className="text-[10px] mt-1 font-medium" style={{ color: theme.textMuted }}>
            {cur.wave_period_s ? `Period ${cur.wave_period_s.toFixed(1)}s · ` : ""}
            {fromCompass !== "—" ? `From ${fromCompass}` : ""}
            {cur.swell_height_m != null ? ` · Swell ${cur.swell_height_m.toFixed(1)}m` : ""}
          </div>
        </div>
      </div>
    </Section>
  );
}

/* ---------------- Flood-watch tile ---------------- */

function FloodWatchTile({ flood, theme }) {
  if (!flood) return null;
  const river = flood.river || {};
  // Only surface when there's a named river within 80 km — GloFAS sub-grid
  // pixels for inland landlocked cities can still flag "severe" but that's
  // confusing without an actual river to watch.
  if (!river.name) return null;
  const sev = flood.severity || {};
  const level = sev.level;
  // Auto-hide for "normal" and "low" — only show when there's something worth flagging
  if (!level || level === "normal" || level === "low_flow") return null;
  const accent =
    level === "severe" ? "#dc2626" :
    level === "watch" ? "#ea580c" :
    level === "elevated" ? "#d97706" :
    "#0891b2";
  const next7 = flood.next7 || {};
  const ratio = next7.ratio_vs_p75;

  return (
    <Section
      title="Flood watch · river discharge"
      accent={accent}
      right={<Pill color={accent} testId="flood-watch-level">{(level || "").toUpperCase()}</Pill>}
      testId="pulse-flood"
      theme={theme}
      delay={0.12}
    >
      <div className="flex items-start gap-3">
        <div
          className="shrink-0 rounded-full flex items-center justify-center"
          style={{ width: 56, height: 56, background: `${accent}1f`, border: `2px solid ${accent}55` }}
        >
          <Drop size={28} weight="duotone" color={accent} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-extrabold leading-tight" style={{ color: theme.textPrimary }}>
            {river.name}
            {river.distance_km != null && river.distance_km > 0.5 && (
              <span className="ml-2 text-[10px] font-bold uppercase tracking-wider" style={{ color: theme.textMuted }}>
                {river.distance_km} km away
              </span>
            )}
          </div>
          <div className="mt-1 text-[11px] font-medium leading-snug" style={{ color: theme.textSecondary }}>
            {sev.label}
            {ratio != null && ` · ${ratio.toFixed(1)}× normal`}
          </div>
          {sev.progress != null && (
            <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: `${accent}22` }}>
              <motion.div
                className="h-full rounded-full"
                style={{ background: accent }}
                initial={{ width: 0 }}
                animate={{ width: `${Math.max(8, Math.min(100, sev.progress * 100))}%` }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              />
            </div>
          )}
          <div className="text-[10px] mt-1.5 font-medium" style={{ color: theme.textMuted }}>
            Copernicus GloFAS · 5 km grid
          </div>
        </div>
      </div>
    </Section>
  );
}

/* ============================================================
 * MAIN
 * ============================================================ */

export default function PulsePage({ confidence, forecast, theme, location, active }) {
  const nowcast = useNowcastTimeline(location);
  const { data: flood } = useFloodRisk(location);
  const [hitRate, setHitRate] = useState(null);
  const [marine, setMarine] = useState(null);

  useEffect(() => {
    if (!active || !location?.latitude || !location?.longitude) return;
    let cancelled = false;
    getHitRate(location.latitude, location.longitude, 60)
      .then((r) => !cancelled && setHitRate(r))
      .catch(() => {});
    getMarine(location.latitude, location.longitude)
      .then((r) => !cancelled && setMarine(r))
      .catch(() => {});
    return () => { cancelled = true; };
  }, [active, location?.latitude, location?.longitude]);

  const hasAnything =
    !!confidence?.convective_alert ||
    !!confidence?.sounding ||
    !!confidence?.heavy_rain ||
    !!confidence?.heat ||
    !!confidence?.model_spread;

  return (
    <div
      className="h-full w-full flex flex-col px-4 pt-2 pb-20"
      data-testid="page-pulse"
      style={{ color: theme.textPrimary }}
    >
      {/* Editorial masthead */}
      <div className="shrink-0 mb-2.5 px-1">
        <div
          className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.32em] font-extrabold"
          style={{ color: theme.textMuted }}
        >
          <Brain size={11} weight="duotone" style={{ color: theme.accent }} />
          Atmosphere Pulse
        </div>
        <h2
          className="font-display tracking-tight leading-[1.1] mt-1"
          style={{
            color: theme.textPrimary,
            fontFamily: "Manrope, system-ui, sans-serif",
            fontSize: 22,
            fontWeight: 800,
            letterSpacing: "-0.04em",
          }}
          data-testid="pulse-headline"
        >
          What the sky is up to
          {location?.name ? (
            <span style={{ color: theme.textSecondary }}> · {location.name}</span>
          ) : null}
        </h2>
        <p
          className="text-[11.5px] mt-0.5 leading-snug max-w-[44ch]"
          style={{ color: theme.textSecondary }}
        >
          A plain-language read on the air above you — every signal we trust.
        </p>
      </div>

      {/* Scroll body */}
      <div
        className="flex-1 min-h-0 overflow-y-auto pr-1 -mr-1 space-y-2.5"
        style={{ overscrollBehavior: "contain", WebkitOverflowScrolling: "touch", scrollbarWidth: "none" }}
        data-testid="pulse-scroll"
      >
        {/* Pro tiles — top-of-page urgency */}
        {nowcast && <StormMotionTile data={nowcast} theme={theme} />}
        <TrustTile stats={hitRate} theme={theme} />
        <ModelAgreementTile forecast={forecast} theme={theme} />

        {/* Existing FriendlyPulse — hero verdict + 6 horizontal comic panels */}
        {!hasAnything && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="rounded-2xl px-4 py-8 text-center"
            style={{
              background: theme.surface,
              border: `1px solid ${theme.surfaceBorder}`,
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
            }}
            data-testid="pulse-empty"
          >
            <Lightning
              size={28}
              weight="duotone"
              style={{ color: theme.textMuted, margin: "0 auto" }}
            />
            <div className="mt-2 text-[12.5px] font-semibold" style={{ color: theme.textPrimary }}>
              Reading the atmosphere…
            </div>
            <div className="text-[11px] mt-1" style={{ color: theme.textMuted }}>
              We're checking storm fuel, moisture, and nearby observations
            </div>
          </motion.div>
        )}

        {hasAnything && <FriendlyPulse confidence={confidence} theme={theme} />}

        {/* Pro tiles — bottom (location-conditional) */}
        <SeaPulseTile marine={marine} theme={theme} />
        <FloodWatchTile flood={flood} theme={theme} />

        <div className="h-2" />
      </div>

      <style>{`[data-testid="pulse-scroll"]::-webkit-scrollbar { display: none; }`}</style>
    </div>
  );
}
